import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors, checkPermission, Print } from 'utils';
import { Table, Pagination, Tooltip, Input, DatePicker, Select, Modal, CheckboxTable, Button } from 'components';
import { CleanIcon, EditIcon, TrashIcon, DownloadIcon } from 'assets/icons';
import { UserService } from 'services';
import moment from 'moment';
import ModalUpdate from './modal-update';

interface Props extends RootState, RouteComponentProps {
	dispatch: any
}

const INITIAL_STATE = {
	search: '',
	since: '',
	until: '',
	plan_id: ''
}

// level_id -> { label, className } para el badge de rol
const ROLE_BADGE: Record<number, { label: string; className: string }> = {
	[Constants.LEVELS.ADMIN]: { label: 'Admin', className: 'role-admin' },
	[Constants.LEVELS.MODERATOR]: { label: 'Moderador', className: 'role-moderator' },
	[Constants.LEVELS.CLIENT]: { label: 'Cliente', className: 'role-client' }
}

const getRoleBadge = (levelId: number) =>
	ROLE_BADGE[levelId] || { label: 'Cliente', className: 'role-client' }

class Users extends React.Component<Props> {

	timer: any = undefined;

	state: any = {
		header: [
			'',
			'Fecha de registro',
			'Nombre',
			'Correo',
			'Rol',
			'Tipo de Membresía',
			'Estatus',
			'Acciones'
		],
		form: INITIAL_STATE,
		data: [],
		page: 1,
		last_page: 1,
		visible: false,
		item: null,
		plans: [],
		selected: new Set<number>(),
		bulkDeleting: false
	}

	componentDidMount() {
		this.props.dispatch({
			type: 'SET_HEADER',
			payload: {
				title: 'Usuarios registrados',
				onBack: null
			}
		});

		this.load();
	}

	load = async (withoutLoading: boolean = false,callback = () => {}) => {
		const res: any = await UserService.admin.get({
			withoutLoading,
			page: this.state.page,
			...this.state.form,
			since: this.state.form.since && moment(this.state.form.since).format('YYYY-MM-DD'),
			until: this.state.form.until && moment(this.state.form.until).format('YYYY-MM-DD'),
		});
		this.setState({
			data: res.users.rows,
			last_page: res.users.count,
			plans: res.plans || []
		});
	}

	change = (e: any,callback = () => {}) => {
		this.setState({
			page: 1,
			form: {
				...this.state.form,
				[e.target.name]: e.target.value
			}
		},callback);
	}

	reset = () => {
		this.setState({
			form: INITIAL_STATE,
			page: 1
		},() => this.load(true));
	}

	edit = (e: any, item: any) => {
		e.preventDefault();
		this.setState({
			visible: true,
			item
		});
	}

	delete = (e: any, item: any) => {
		if (e) {
			e.preventDefault();
		}
		Globals.confirm('¿Desea eliminar el usuario?',async () => {
			await UserService.admin.delete({
				_user_id: item.id
			});
			this.load(true);
			this.setState({
				visible: false,
				item: null
			});
		});
	}

	toggleRow = (e: any, user_id: number) => {
		if (e && e.stopPropagation) e.stopPropagation();
		const selected = new Set<number>(this.state.selected);
		if (selected.has(user_id)) selected.delete(user_id);
		else selected.add(user_id);
		this.setState({ selected });
	}

	toggleAllVisible = () => {
		const visibleIds: number[] = (this.state.data || []).map((u: any) => u.id);
		const selected = new Set<number>(this.state.selected);
		const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
		if (allSelected) {
			visibleIds.forEach((id) => selected.delete(id));
		} else {
			visibleIds.forEach((id) => selected.add(id));
		}
		this.setState({ selected });
	}

	allVisibleSelected = (): boolean => {
		const visibleIds: number[] = (this.state.data || []).map((u: any) => u.id);
		if (visibleIds.length === 0) return false;
		return visibleIds.every((id) => this.state.selected.has(id));
	}

	clearSelection = () => {
		this.setState({ selected: new Set<number>() });
	}

	bulkDelete = () => {
		const ids: number[] = Array.from(this.state.selected);
		if (ids.length === 0) return;
		if (ids.length > 50) {
			Globals.showError('No se pueden eliminar más de 50 usuarios a la vez');
			return;
		}
		Globals.confirm(`¿Desea eliminar ${ ids.length } usuario${ ids.length === 1 ? '' : 's' } seleccionado${ ids.length === 1 ? '' : 's' }? Esta acción no se puede deshacer.`, async () => {
			this.setState({ bulkDeleting: true });
			try {
				const res: any = await UserService.admin.bulkDelete({
					user_ids: ids,
					confirm: 'DELETE_USERS_CONFIRMED'
				});
				const deleted = res?.deleted_count ?? ids.length;
				const cascaded = res?.cascaded_subuser_count ?? 0;
				const msg = cascaded > 0
					? `Se eliminaron ${ deleted } usuario${ deleted === 1 ? '' : 's' } y ${ cascaded } subusuario${ cascaded === 1 ? '' : 's' } asociado${ cascaded === 1 ? '' : 's' }`
					: `Se eliminaron ${ deleted } usuario${ deleted === 1 ? '' : 's' } correctamente`;
				Globals.showSuccess(msg);
				this.setState({
					selected: new Set<number>(),
					bulkDeleting: false
				}, () => this.load(true));
			} catch (error: any) {
				this.setState({ bulkDeleting: false });
				Globals.showError(error?.message || 'No se pudieron eliminar los usuarios');
			}
		});
	}

	download = async () => {
		const res: any = await UserService.admin.download({
			...this.state.form,
			since: this.state.form.since && moment(this.state.form.since).format('YYYY-MM-DD'),
			until: this.state.form.until && moment(this.state.form.until).format('YYYY-MM-DD'),
		});
	    Print(Constants.BASE_STORAGE + res.url);
	}

	onClose = () => {
		this.setState({
			visible: false,
			item: null
		},() => this.load(true));
	}

	render() {
		const { visible } = this.state;
		const plan_free: any = this.state.plans?.find((i: any) => i.id == Constants.PLANS.FREE);
		const selectedCount: number = this.state.selected.size;
		const canBulkDelete: boolean = !!checkPermission(Constants.MODULES.USERS, 'delete');

		return (
			<div id="admin-users">
				<Modal
		          className="modal-update-user"
		          visible={ visible }
		          title="Editar"
		          onClose={ () => this.onClose() }
		        >
		          <ModalUpdate
		          	onDelete={ () => {
		          		this.delete(null,this.state.item);
		          	} }
		          	plan_free={ plan_free }
		            item={ this.state.item }
		            onClose={ () => this.onClose() } />
		      	</Modal>

				<div className="row row-filter">
					<div className="col-md-12">
						<div className="row">
							<div className="col-md-11 row">
								<div className="col-md-4">
									<Input
										value={ this.state.form.search }
										name="search"
										placeholder="Buscar"
										label="Buscar por nombre o correo"
										className="input-table"
										onSubmit={ () => this.load() }
										onChange={ (e: any) => {
											clearTimeout(this.timer);
											this.timer = setTimeout(() => {
												this.load(true);
											},1000);
											this.change(e); 
										} } />
								</div>
								<div className="col-md">
									<Select
										color="white"
										placeholder="Seleccionar"
										label="Tipo de Membresía"
										name="plan_id"
										onChange={ (e: any) => this.change(e,async () => {
											this.load(true);
										}) }
										value={ this.state.form.plan_id }
										options={ this.state.plans?.map((item: any) => {
											return {
												value: item.id,
												label: item.name
											}
										}) } />
								</div>
								<div className="col-md">
									<DatePicker
										label="Desde"
										placeholder="Desde"
										onChange={ (text: string) => {
											this.change({
												target: {
													value: text,
													name: 'since'
												}
											},() => this.load(true));
										} }
										value={ this.state.form.since }
									/>
								</div>
								<div className="col-md">
									<DatePicker
										label="Hasta"
										placeholder="Hasta"
										onChange={ (text: string) => {
											this.change({
												target: {
													value: text,
													name: 'until'
												}
											},() => this.load(true));
										} }
										value={ this.state.form.until }
									/>
								</div>
							</div>
							<div className="col-md-1">
								{
									this.state.form != INITIAL_STATE && (
										<button className="btn btn-clean" onClick={ () => this.reset() }>
											<img src={ CleanIcon } />
										</button>
									)
								}
							</div>
						</div>
					</div>
				</div>

				{ canBulkDelete && selectedCount > 0 && (
					<div className="bulk-action-bar">
						<div className="bulk-info">
							<span className="count">{ selectedCount }</span>
							<span className="bulk-label">{ selectedCount === 1 ? 'usuario seleccionado' : 'usuarios seleccionados' }</span>
						</div>
						<div className="bulk-actions">
							<Button
								type="button"
								className="btn-clear"
								onClick={ this.clearSelection }
								disabled={ this.state.bulkDeleting }>
								Limpiar
							</Button>
							<Button
								type="button"
								className="btn-bulk-delete"
								onClick={ this.bulkDelete }
								disabled={ this.state.bulkDeleting }>
								<img src={ TrashIcon } />
								{ this.state.bulkDeleting ? 'Eliminando...' : `Eliminar (${ selectedCount })` }
							</Button>
						</div>
					</div>
				) }

				<Table title="Usuarios registrados" data={ this.state.data.length } header={ this.state.header } rightAlign={ [7] } titleRight={
					<>
						{
							this.state.data.length > 0 && (
								<button className="container-add-table" onClick={ () => this.download() }>
									<img src={ DownloadIcon } />
									<p>Descargar Excel</p>
								</button>
							)
						}
					</>
				}>
					{ canBulkDelete && this.state.data.length > 0 && (
						<tr className="row-master-select">
							<td className="td-checkbox">
								<CheckboxTable
									active={ this.allVisibleSelected() }
									onClick={ this.toggleAllVisible } />
							</td>
							<td colSpan={ 7 } className="master-select-label">
								{ this.allVisibleSelected()
									? 'Todos los usuarios visibles están seleccionados'
									: 'Seleccionar todos los usuarios visibles' }
							</td>
						</tr>
					) }
					{ this.state.data.map((i: any,index: number) => {
						const active_plan = i.plan_expiration_date > moment().format('YYYY-MM-DD');
						const isSelected = this.state.selected.has(i.id);

						return (
							<tr key={ index } className={ isSelected ? 'row-selected' : '' }>
								<td className="td-checkbox" onClick={ (e: any) => e.stopPropagation() }>
									{ canBulkDelete && (
										<CheckboxTable
											active={ isSelected }
											onClick={ (e: any) => this.toggleRow(e, i.id) } />
									) }
								</td>
								<td>{ moment(i.created_at).format('DD-MM-YYYY') }</td>
								<td>{ i.name + ' ' + i.lastname }</td>
								<td>{ i.email }</td>
								<td>
									{ (() => {
										const badge = getRoleBadge(i.level_id);
										return (
											<span className={ `role-badge ${ badge.className }` }>
												{ badge.label }
											</span>
										);
									})() }
								</td>
								<td>{ i.plan_name || plan_free?.name }</td>
								<td>
									<div className={ `container-status ${ active_plan || !i.plan_name ? '' : 'danger' }` }>
										<p>{ active_plan || !i.plan_name ? 'Activa' : 'Inactiva' }</p>
									</div>
								</td>
								<td className="text-right">
									{
										checkPermission(Constants.MODULES.USERS,'write') && (
											<Tooltip title="Editar">
												<a className="link-icon" href="#" onClick={ (e: any) => this.edit(e,i) }>
													<img src={ EditIcon } />
												</a>
											</Tooltip>
										)
									}
									{
										checkPermission(Constants.MODULES.USERS,'delete') && (
											<Tooltip title="Eliminar">
												<a className="link-icon" href="#" onClick={ (e: any) => this.delete(e,i) }>
													<img src={ TrashIcon } />
												</a>
											</Tooltip>
										)
									}
								</td>
							</tr>
						)
					}) }
				</Table>

				<Pagination 
					pages={ this.state.last_page } 
					active={ this.state.page }
					onChange={ async (page: number) => {
						await this.setState({
							page: page
						});
						this.load();
					} } />
			</div>
		)
	}
}

export default connect((state: RootState) => {
	return {
		user: state.user
	}
})(Users);